import Paginator from '@/components/Paginator/Paginator';
import { UserService } from '@/services/UserService';
import { DefaultRowsPerPage } from '@/utils/constant/constant';
import { CHECK_STATUS, ORDER_STATUS, ORDER_STATUS_OPTIONS } from '@/utils/helper';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import { toast } from 'react-toastify';
import '../../../assets/styles/react-datepicker.css';
import './OrderHistory.scss';

type ToastOrderDetail = {
  id: number;
  display_name: string;
  quantity: number;
  price: string;
  discount_amount: string;
  pre_discount_price: string;
  receipt_line_price: string;
  tax: string;
};

type ToastCheck = {
  id: number;
  display_number: string;
  tab_name: string | null;
  payment_status: string;
  paid_date: string;
  amount: string;
  total_amount: string;
  tax_amount: string;
  number_of_guests: number;
  tip_amount: string;
  discount_amount: string;
  invoice_file: string;
  member: {
    id: number;
    first_name: string;
    last_name: string;
    club_membership_id: string;
  };
  sub_member: {
    id: number;
    first_name: string;
    last_name: string;
    club_membership_id: string;
  };
  toastOrderDetails: ToastOrderDetail[];
};

type Order = {
  id: number;
  display_number: number;
  source: string;
  business_date: string;
  amount: string;
  tip_amount: string;
  discount_amount: string;
  tax_amount: string;
  number_of_guests: number;
  payment_status: string;
  created_at: string;
  toastChecks: ToastCheck[];
};

function OrderHistory() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [totalOrders, setTotalOrders] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [dateChanged, setDateChanged] = useState<number>(0);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [invoiceFiles, setInvoiceFiles] = useState<string[]>([]);
  const [downloadInvoiceLoading, setDownloadInvoiceLoading] = useState(false);
  const orderDetails = useRef<HTMLDivElement | null>(null);
  const handleSelectOrder = (order: Order) => {
    setSelectedOrder(order);
    setInvoiceFiles(
      order.toastChecks.reduce((acc: string[], check) => {
        if (check.invoice_file) {
          acc.push(check.invoice_file);
        }
        return acc;
      }, [])
    );

    // Check if the top of profile-content is visible in viewport
    setTimeout(() => {
      if (orderDetails.current) {
        const rect = orderDetails.current.getBoundingClientRect();
        const isTopVisible = rect.top >= 0 && rect.top <= window.innerHeight;
        // Only scroll if the top part is not visible
        if (!isTopVisible) {
          orderDetails.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }
    }, 0);
  };

  const handleBackToList = () => {
    setSelectedOrder(null);
    setInvoiceFiles([]);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleDownloadInvoice = () => {
    setDownloadInvoiceLoading(true);
    UserService.downloadFiles(invoiceFiles)
      .then(async (res) => {
        if (res?.data?.files.length) {
          for (const file of res.data.files) {
            const response = await fetch(file.url);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `#${selectedOrder?.display_number}_${selectedOrder?.business_date}_${file.name}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }
        }
      })
      .catch((err) => {
        console.log('err while downloading invoice ========= ', err);
        toast.error(
          'Some error occurred while downloading invoice, please try later.'
        );
      })
      .finally(() => {
        setDownloadInvoiceLoading(false);
      });
  };

  const fetchOrders = () => {
    UserService.getOrders(
      DefaultRowsPerPage,
      page,
      startDate ? moment(startDate).format('YYYY-MM-DD') : '',
      endDate ? moment(endDate).format('YYYY-MM-DD') : '',
      selectedStatus
    ).then((res) => {
      if (res?.data?.data?.rows) {
        setOrders(res.data.data.rows);
        setTotalOrders(res.data.data.count);
      }
    });
  };

  useEffect(() => {
    fetchOrders();
  }, [page, selectedStatus, dateChanged]);

  const onChangeDate = (dates: any) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    if ((start && end) || (!start && !end)) {
      setPage(1);
      setDateChanged(dateChanged + 1);
    }
  };

  return (
    <div className="profile-main order-history-list">
      {/* Show order list only if no order is selected */}
      {!selectedOrder && (
        <div className="profile-content">
          <div className="head-profile">
            <h1 className="page-title">ORDER HISTORY</h1>
            <div className="filter-right-profile">
              <div className="form-group multiple-datepicker">
                <div className="form-group">
                  <Select
                    options={ORDER_STATUS_OPTIONS}
                    placeholder="Filter by Status"
                    classNamePrefix="cs-select"
                    isClearable={selectedStatus !== ''}
                    value={ORDER_STATUS_OPTIONS.find(
                      (option) => option.value === selectedStatus
                    )}
                    onChange={(e: any) => {
                      setPage(1);
                      setSelectedStatus(e?.value || '');
                    }}
                    isSearchable={false}
                  />
                </div>
                <DatePicker
                  onChangeRaw={(e: any) => e.preventDefault()}
                  selected={startDate}
                  startDate={startDate}
                  endDate={endDate}
                  maxDate={new Date()}
                  onChange={onChangeDate}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="Select date"
                  showMonthDropdown
                  showYearDropdown
                  isClearable
                  selectsRange
                  showIcon
                  customInput={
                    <input
                      inputMode="none"
                      readOnly
                    />
                  }
                />
              </div>
            </div>
          </div>

          <div className="order-content">
            {orders.length === 0 ? (
              <div className="no-record-found">No record found</div>
            ) : (
              orders.map((order) => (
                <div
                  className="order-list-item"
                  key={order.id}
                  onClick={() => handleSelectOrder(order)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="order-list-head">
                    <div className="id">#{order.display_number}</div>
                    <div className="member-lists-history">
                      Checked-In Member
                    </div>
                    <div className="payment-info">
                      <span
                        className={`status status-${order.payment_status.toLowerCase()}`}
                      >
                        {
                          ORDER_STATUS[
                            order.payment_status as keyof typeof ORDER_STATUS
                          ]
                        }
                      </span>
                      <span className="price">${order.amount}</span>
                    </div>
                  </div>
                  <div className="order-list-body">
                    <p>
                      {order.toastChecks
                        ?.map((check) =>
                          check.toastOrderDetails
                            ?.map(
                              (detail) =>
                                `${detail.quantity} ${detail.display_name}`
                            )
                            .join(', ')
                        )
                        .join(', ')}
                    </p>
                    <div className="member-lists-history-cell">
                      <div className="member-lists-history-cell-title">
                        Checked-In Member
                      </div>
                      <ul>
                        {order.toastChecks
                          ?.map((check) =>
                            check.sub_member
                              ? `${check.sub_member.first_name} ${check.sub_member.last_name}`
                              : `${check.member.first_name} ${check.member.last_name}`
                          )
                          .filter(
                            (name, idx, self) => self.indexOf(name) === idx
                          )
                          .map((name) => (
                            <li key={name}>{name}</li>
                          ))}
                      </ul>
                    </div>
                    <div className="payment-date">
                      <time>{formatDate(order.business_date)}</time>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          {totalOrders > DefaultRowsPerPage && (
            <Paginator
              count={DefaultRowsPerPage}
              totalCount={totalOrders}
              currentPage={page}
              onPageChange={setPage}
            />
          )}
        </div>
      )}

      {/* Order Details Section */}
      {selectedOrder && (
        <div className="profile-content" ref={orderDetails}>
          <div className="head-profile">
            <h1 className="page-title">ORDER DETAILS</h1>
            <div className="order-head-right">
              <button className="btn-text" onClick={handleBackToList}>
                <i className="cs-icon icon-arrow-left"></i> Back to Orders
              </button>
              {invoiceFiles.length > 0 && (
                <button
                  className="btn btn--pri btn--sm"
                  onClick={handleDownloadInvoice}
                  disabled={downloadInvoiceLoading}
                >
                  Download Invoice
                </button>
              )}
            </div>
          </div>

          {selectedOrder.toastChecks.length > 1 && (
            <div className="order-detail-main">
              <div className="item">
                <label>Total Checks</label>
                <div className="value">{selectedOrder.toastChecks.length}</div>
              </div>
              <div className="item">
                <label>TAXES</label>
                <div className="value">{selectedOrder.tax_amount}</div>
              </div>
              <div className="item">
                <label>TOTAL</label>
                <div className="value">{selectedOrder.amount}</div>
              </div>
              <div className="item date-item">
                <label>{formatDate(selectedOrder.business_date)}</label>
                <div className="value">
                  <span className={`status status-${selectedOrder.payment_status.toLowerCase()}`}>
                    {
                      ORDER_STATUS[
                        selectedOrder.payment_status as keyof typeof ORDER_STATUS
                      ]
                    }
                  </span>
                </div>
              </div>
            </div>
          )}

          {selectedOrder.toastChecks?.map((check) => {
            return (
              <div className="order-detail">
                <div className="order-detail-head">
                  <div className="order-detail-id">#{check.display_number}</div>
                  <div className="order-detail-head-right">
                    <div className="date">
                      {formatDate(selectedOrder.business_date)}
                    </div>
                    <div
                      className={`status status-${check.payment_status.toLowerCase()}`}
                    >
                      {
                        CHECK_STATUS[
                          check.payment_status as keyof typeof CHECK_STATUS
                        ]
                      }
                    </div>
                  </div>
                </div>

                <div className="order-detail-body">
                  <table>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>PRICE</th>
                        <th>QTY</th>
                        <th>TOTAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {check.toastOrderDetails?.map((detail) => {
                        return (
                          <tr key={detail.id}>
                            <td>{detail.display_name}</td>
                            <td>
                              $
                              {(
                                +detail.pre_discount_price / +detail.quantity
                              ).toFixed(2)}
                            </td>
                            <td>{detail.quantity}</td>
                            <td>${detail.pre_discount_price}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <div className="total-block">
                    <div className="total-item">
                      <label>Subtotal</label>
                      <span>
                        ${(+check.amount + +check.discount_amount).toFixed(2)}
                      </span>
                    </div>
                    <div className="total-item">
                      <label>TAXES</label>
                      <span>${check.tax_amount}</span>
                    </div>
                    <div className="total-item">
                      <label>DISCOUNT</label>
                      <span>${check.discount_amount}</span>
                    </div>
                    <div className="total-item">
                      <label>TIP</label>
                      <span>${check.tip_amount}</span>
                    </div>
                    <div className="total-item">
                      <label>Total</label>
                      <span>${check.total_amount}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default OrderHistory;
